import { forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2',
    'font-medium rounded-md transition-colors',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
    'disabled:opacity-50 disabled:pointer-events-none',
    'select-none',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-primary-500 text-white',
          'hover:bg-primary-600',
          'active:bg-primary-700',
        ],
        // alias for default - used in LoginForm
        primary: [
          'bg-primary-500 text-white',
          'hover:bg-primary-600',
          'active:bg-primary-700',
        ],
        secondary: [
          'bg-slate-100 text-slate-900',
          'hover:bg-slate-200',
          'active:bg-slate-300',
        ],
        outline: [
          'border border-slate-300 text-slate-700 bg-white',
          'hover:bg-slate-50 hover:border-slate-400',
          'active:bg-slate-100',
        ],
        ghost: [
          'text-slate-600',
          'hover:bg-slate-100 hover:text-slate-900',
          'active:bg-slate-200',
        ],
        destructive: [
          'bg-danger-500 text-white',
          'hover:bg-danger-600',
          'active:bg-danger-700',
        ],
        // alias for destructive
        danger: [
          'bg-danger-500 text-white',
          'hover:bg-danger-600',
          'active:bg-danger-700',
        ],
        link: [
          'text-primary-500 underline-offset-4',
          'hover:underline',
          'h-auto p-0',
        ],
      },
      size: {
        sm: 'h-7 px-2.5 text-xs',
        default: 'h-9 px-4 text-sm',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-sm',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled ?? loading}
        {...props}
      >
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        {children}
      </Comp>
    )
  }
)

Button.displayName = 'Button'

export { buttonVariants }
